import React, { useMemo } from 'react';
import { ButtonTW } from '../../../../../../../tailwindComponents/buttonTW/ButtonTW';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { Show } from '../../../../../../shared/Show';
import { UUID } from '../../../../../../../types/shared/UUID';
import { checkForMutationSuccess } from '../../../../../../../API/checkForMutationSuccess';
import { useSendAllDocumentsMutation } from '../../../../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { useParams } from 'react-router-dom';
import { ISigningAppointmentDocumentResponse } from '../../../../../../../types/signingAppointment/ISigningAppointmentDocumentResponse';
import { hasMarkedForDeletionDocuments, hasSentDocuments, hasUnsentDocuments } from '../Utils';
import { SpinnerTW } from 'tailwindComponents/spinnerTW/SpinnerTW';
import { useSelectAppointmentFromPathParam } from 'utilities/order/scheduling/useSomeAppointmentFromParam';
import { Tooltip } from '../../../../../../../ui-toolkit';

const getLabel = (documents: ISigningAppointmentDocumentResponse[]): string | null => {
  const appointmentHasSentDocuments = hasSentDocuments(documents);
  if (appointmentHasSentDocuments) {
    return 'Sync Documents';
  }
  return 'Send All Documents';
};

export const SendDocumentsButton: React.FC<{
  outgoingDocs: ISigningAppointmentDocumentResponse[];
}> = ({ outgoingDocs }) => {
  const { orderId, appointmentId } = useParams<{ orderId: UUID; appointmentId: UUID }>();
  const [sendAllDocumentsMutation, { isLoading: isLoadingDocumentsMutation }] = useSendAllDocumentsMutation();
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();
  const sendDocsLabel = useMemo(() => getLabel(outgoingDocs), [outgoingDocs]);
  const appointmentHasUnsentDocuments = useMemo(() => hasUnsentDocuments(outgoingDocs), [outgoingDocs]);
  const appointmentHasDocumentsMarkedForDeletion = useMemo(
    () => hasMarkedForDeletionDocuments(outgoingDocs),
    [outgoingDocs]
  );

  const onSendToSigSyncClick = async () => {
    const res = await sendAllDocumentsMutation({
      signingAppointmentId: appointmentId,
      orderId,
    });

    checkForMutationSuccess(res, 'sending documents to signature sync');
  };

  const shouldShowSendDocumentsButton = !!appointmentFromPath?.isSignatureSyncAppointment;

  return (
    <Show when={shouldShowSendDocumentsButton}>
      <Tooltip
        trigger="hover"
        placement="bottom"
        offset="0.5rem"
        tooltipContent={
          <div className="max-w-100">
            <p>Clicking this button deletes marked documents,</p>
            <p>sends newly added unsent documents, and retains already</p>
            <p>sent non-deleted documents in the portal.</p>
          </div>
        }
      >
        <ButtonTW
          disabled={
            (!appointmentHasUnsentDocuments && !appointmentHasDocumentsMarkedForDeletion) || isLoadingDocumentsMutation
          }
          className="flex font-medium capitalize text-white ring-border-default-content"
          onClick={() => onSendToSigSyncClick()}
        >
          <Show
            when={!isLoadingDocumentsMutation}
            fallback={
              <section className="pr-2">
                <SpinnerTW />
              </section>
            }
          >
            <FontAwesomeIcon
              cursor="pointer"
              className="text-white-600 h-4 w-4 pr-2"
              data-test-id="resend-sad-button"
              icon={faPaperPlane}
            />
          </Show>
          <span>{sendDocsLabel}</span>
        </ButtonTW>
      </Tooltip>
    </Show>
  );
};
